
import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { Provider, Service, Booking } from '../types/model';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate, useLocation } from 'react-router-dom';

// Enhanced types to match our requirements
type StaffMember = {
  id: string;
  name: string;
  position: string;
  imageUrl?: string;
  specialties?: string[];
};

type AddOn = {
  id: string;
  name: string;
  price: number;
  durationAdded: number;
  description: string;
};

interface BookingContextType {
  selectedProvider: Provider | null;
  selectedService: Service | null;
  selectedDate: Date | null;
  selectedTime: string | null;
  selectedStaff: StaffMember | null;
  bookingAddons: AddOn[];
  customNote: string;
  customImage: File | null;
  setSelectedProvider: (provider: Provider | null) => void;
  setSelectedService: (service: Service | null) => void;
  setSelectedDate: (date: Date | null) => void;
  setSelectedTime: (time: string | null) => void;
  setSelectedStaff: (staff: StaffMember | null) => void;
  setBookingAddons: (addons: AddOn[]) => void;
  addAddon: (addon: AddOn) => void;
  removeAddon: (addonId: string) => void;
  setCustomNote: (note: string) => void;
  setCustomImage: (image: File | null) => void;
  resetBooking: () => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  isBookingComplete: boolean;
  setIsBookingComplete: (complete: boolean) => void;
  completeBooking: () => void;
  bookingReference: string | null;
  showAddonsStep: boolean;
  isBookingStarted: boolean;
  startBooking: () => void;
  getTotalPrice: () => number;
  getTotalDuration: () => number;
  contactInfo: {
    name: string;
    email: string;
    phone: string;
  };
  setContactInfo: (info: {name: string, email: string, phone: string}) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  canMoveToNextStep: () => boolean;
}

const BookingContext = createContext<BookingContextType | undefined>(undefined);

export const BookingProvider = ({ children }: { children: ReactNode }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Main booking state
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
  const [bookingAddons, setBookingAddons] = useState<AddOn[]>([]);
  const [customNote, setCustomNote] = useState<string>('');
  const [customImage, setCustomImage] = useState<File | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isBookingComplete, setIsBookingComplete] = useState(false);
  const [bookingReference, setBookingReference] = useState<string | null>(null);
  const [isBookingStarted, setIsBookingStarted] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  
  // Determine if we should show addons step based on provider configuration
  const showAddonsStep = selectedProvider?.aktivierte_addons && 
    selectedProvider.aktivierte_addons.length > 0;

  // Verbesserte Logik für den Abschluss der Buchung und Navigation zur Bestätigungsseite
  useEffect(() => {
    const path = location.pathname;
    
    // Debug: Protokolliere die aktuellen Buchungsdaten und den Pfad
    console.log("Booking context check - Path:", path);
    console.log("Booking data check:", {
      provider: selectedProvider?.name || "none",
      service: selectedService?.name || "none", 
      isComplete: isBookingComplete,
      reference: bookingReference
    });
    
    // Wenn wir auf der Bestätigungsseite sind und die Buchung als abgeschlossen markiert ist,
    // aber die Referenz fehlt, generieren wir eine.
    if (path === '/booking/complete' && isBookingComplete && !bookingReference) {
      const reference = `BK-${Math.floor(100000 + Math.random() * 900000)}`;
      console.log("Generated new reference on complete page (missing reference):", reference);
      setBookingReference(reference);
    }
    
    // Wenn wir auf der Bestätigungsseite sind, aber die Buchung nicht als abgeschlossen markiert ist,
    // markieren wir sie als abgeschlossen und generieren eine Referenz
    if (path === '/booking/complete' && !isBookingComplete) {
      setIsBookingComplete(true);
      if (!bookingReference) {
        const reference = `BK-${Math.floor(100000 + Math.random() * 900000)}`;
        console.log("Setting booking as complete and generating reference:", reference);
        setBookingReference(reference);
      }
    }
  }, [isBookingComplete, bookingReference, location.pathname]);

  // Self-healing logic to fix broken booking steps
  // Move back to step 1 if provider is not selected in step 2
  useEffect(() => {
    if (currentStep === 2 && !selectedProvider) {
      console.log('Self-healing: Moving back to step 1 because no provider is selected');
      setCurrentStep(1);
    }
  }, [currentStep, selectedProvider]);

  // Move back to step 2 if required information for step 3 is missing
  useEffect(() => {
    if (currentStep === 3 && (!selectedService || !selectedStaff || !selectedDate || !selectedTime)) {
      console.log('Self-healing: Moving back to step 2 because required information is missing');
      setCurrentStep(2);
    }
  }, [currentStep, selectedService, selectedStaff, selectedDate, selectedTime]);

  // Function to determine if the user can proceed to the next step
  const canMoveToNextStep = useCallback(() => {
    console.log("Checking if can move to next step, current step:", currentStep);
    console.log("Selected provider:", selectedProvider?.name || 'none');
    console.log("Selected service:", selectedService?.name || 'none');
    console.log("Selected staff:", selectedStaff?.name || 'none');
    console.log("Selected date:", selectedDate?.toDateString() || 'none');
    console.log("Selected time:", selectedTime || 'none');
    
    switch (currentStep) {
      case 1: // Provider selection
        return !!selectedProvider;
      case 2: // Service, Staff, Date selection
        return !!selectedService && !!selectedStaff && !!selectedDate && !!selectedTime;
      case 3: // Confirmation
        return !!(contactInfo.name && contactInfo.email && contactInfo.phone); // Fixed: Ensures boolean return
      default:
        return false;
    }
  }, [currentStep, selectedProvider, selectedService, selectedStaff, selectedDate, selectedTime, contactInfo]);

  // Helper functions to navigate through steps
  const goToNextStep = useCallback(() => {
    console.log(`Trying to move from step ${currentStep} to next step`);
    console.log(`canMoveToNextStep returns: ${canMoveToNextStep()}`);
    
    if (canMoveToNextStep()) {
      console.log(`Moving from step ${currentStep} to ${currentStep + 1}`);
      setCurrentStep(prevStep => prevStep + 1);
      
      // Provide user feedback
      let stepMessage = "";
      if (currentStep === 1) {
        stepMessage = "Wähle nun Service, Personal & Termin.";
      } else if (currentStep === 2) {
        stepMessage = "Bestätige nun deine Buchung.";
      }
      
      if (stepMessage) {
        toast({
          title: `Schritt ${currentStep + 1}`,
          description: stepMessage,
          duration: 3000,
        });
      }
    } else {
      console.log("Cannot move to next step. Missing required selections:", currentStep);
      
      let errorMessage = "Um fortzufahren, fülle bitte alle erforderlichen Felder aus.";
      
      if (currentStep === 2) {
        // Be more specific about what's missing
        const missingItems = [];
        if (!selectedService) missingItems.push("Leistung");
        if (!selectedStaff) missingItems.push("Mitarbeiter");
        if (!selectedDate || !selectedTime) missingItems.push("Termin");
        
        if (missingItems.length > 0) {
          errorMessage = `Bitte wähle noch: ${missingItems.join(", ")}`;
        }
      }
      
      toast({
        title: "Bitte wähle alle benötigten Informationen",
        description: errorMessage,
        duration: 3000,
      });
    }
  }, [canMoveToNextStep, currentStep, selectedProvider, selectedService, selectedStaff, selectedDate, selectedTime, toast]);

  const goToPreviousStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prevStep => prevStep - 1);
    } else {
      navigate('/');
    }
  }, [currentStep, navigate]);

  const resetBooking = useCallback(() => {
    // Pfad prüfen, bevor zurückgesetzt wird
    const path = location.pathname;
    
    // Wenn wir auf der Bestätigungsseite sind, NICHT zurücksetzen!
    if (path === '/booking/complete') {
      console.log("Skipping booking reset on complete page");
      return;
    }
    
    console.log("Resetting booking data");
    setSelectedProvider(null);
    setSelectedService(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedStaff(null);
    setBookingAddons([]);
    setCustomNote('');
    setCustomImage(null);
    setCurrentStep(1);
    setIsBookingComplete(false);
    setBookingReference(null);
    setIsBookingStarted(false);
    setContactInfo({
      name: '',
      email: '',
      phone: ''
    });
  }, [location.pathname]);

  const startBooking = useCallback(() => {
    setIsBookingStarted(true);
  }, []);

  const addAddon = useCallback((addon: AddOn) => {
    setBookingAddons(prev => [...prev, addon]);
  }, []);

  const removeAddon = useCallback((addonId: string) => {
    setBookingAddons(prev => prev.filter(addon => addon.id !== addonId));
  }, []);

  const getTotalPrice = useCallback(() => {
    const basePrice = selectedService?.preis || 0;
    const addonsPrice = bookingAddons.reduce((sum, addon) => sum + addon.price, 0);
    return basePrice + addonsPrice;
  }, [selectedService, bookingAddons]);

  const getTotalDuration = useCallback(() => {
    const baseDuration = selectedService?.dauer || 0;
    const addonsDuration = bookingAddons.reduce((sum, addon) => sum + addon.durationAdded, 0);
    return baseDuration + addonsDuration;
  }, [selectedService, bookingAddons]);

  const completeBooking = useCallback(() => {
    // Generate random booking reference
    const reference = `BK-${Math.floor(100000 + Math.random() * 900000)}`;
    console.log("Completing booking with reference:", reference);
    setBookingReference(reference);
    setIsBookingComplete(true);
    
    toast({
      title: "Buchung erfolgreich!",
      description: `Deine Buchung ${reference} wurde erfolgreich abgeschlossen.`,
      duration: 5000,
    });

    // Bessere Navigationsstrategie mit einer kurzen Verzögerung
    setTimeout(() => {
      // Sicherstellen, dass die Navigation nur einmal erfolgt
      if (location.pathname !== '/booking/complete') {
        console.log("Navigating to complete page");
        navigate('/booking/complete');
      }
    }, 300);
  }, [navigate, toast, location.pathname]);

  // Wrapped version of setSelectedProvider that also logs
  const wrappedSetSelectedProvider = useCallback((provider: Provider | null) => {
    console.log("Setting selected provider to:", provider?.name || 'null');
    setSelectedProvider(provider);
  }, []);

  // Deep check for state changes that might be affecting navigation
  useEffect(() => {
    if (selectedProvider) {
      console.log("Provider state updated:", selectedProvider.name);
    }
  }, [selectedProvider]);

  return (
    <BookingContext.Provider
      value={{
        selectedProvider,
        selectedService,
        selectedDate,
        selectedTime,
        selectedStaff,
        bookingAddons,
        customNote,
        customImage,
        setSelectedProvider: wrappedSetSelectedProvider,
        setSelectedService,
        setSelectedDate,
        setSelectedTime,
        setSelectedStaff,
        setBookingAddons,
        addAddon,
        removeAddon,
        setCustomNote,
        setCustomImage,
        resetBooking,
        currentStep,
        setCurrentStep,
        isBookingComplete,
        setIsBookingComplete,
        completeBooking,
        bookingReference,
        showAddonsStep,
        isBookingStarted,
        startBooking,
        getTotalPrice,
        getTotalDuration,
        contactInfo,
        setContactInfo,
        goToNextStep,
        goToPreviousStep,
        canMoveToNextStep,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};
